import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ChartLineUp,
  Users,
  Briefcase,
  Lightbulb,
  Handshake,
  Crown,
  MagnifyingGlass,
  Chalkboard,
  Rocket,
  Gear,
  UsersThree,
  Medal,
  ArrowRight,
  CheckCircle,
  Buildings,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const services = [
  {
    icon: ChartLineUp,
    title: "Empirical Research",
    description: "We carry out research to identify pressing community and enterprise needs, generating knowledge that guides practical and sustainable interventions.",
    features: [
      "Needs assessment studies",
      "Market research and analysis",
      "Knowledge documentation",
      "Best practices sharing",
      "Policy research and advocacy",
    ],
    color: "green",
  },
  {
    icon: Users,
    title: "Capacity Building",
    description: "We equip local entrepreneurs, enterprises, and communities with practical skills, specialized training, and resources needed for growth and resilience.",
    features: [
      "Business management training",
      "Financial literacy programs",
      "Technical skills development",
      "Organizational strengthening",
      "Mentorship and coaching",
    ],
    color: "amber",
  },
  {
    icon: Briefcase,
    title: "Enterprise Development",
    description: "We support community-led enterprises in transforming innovative ideas into actionable projects and impactful real-world solutions.",
    features: [
      "Business plan development",
      "Access to finance support",
      "Market linkages",
      "Value chain integration",
      "Enterprise incubation",
    ],
    color: "green",
  },
  {
    icon: Lightbulb,
    title: "Technological Innovation",
    description: "We promote suitable and cutting-edge technologies that strengthen entrepreneurial ventures and improve productivity across sectors.",
    features: [
      "Digital skills training",
      "Technology adoption support",
      "Innovation hubs",
      "ICT for development",
      "E-commerce enablement",
    ],
    color: "amber",
  },
  {
    icon: Handshake,
    title: "Leadership Development",
    description: "Our leadership programs emphasize community ownership, active participation, and transformational leadership for long-term sustainability.",
    features: [
      "Leadership training programs",
      "Executive coaching",
      "Youth leadership development",
      "Women leadership programs",
      "Succession planning support",
    ],
    color: "green",
  },
  {
    icon: Crown,
    title: "Business Support Services",
    description: "We provide training in business management, financial literacy, marketing development systems, and other essential skills for enterprise success.",
    features: [
      "Business planning support",
      "Financial management training",
      "Marketing strategy development",
      "Customer service excellence",
      "Operational efficiency",
    ],
    color: "amber",
  },
  {
    icon: Buildings,
    title: "B2B Training",
    description: "We provide customized business-to-business training programs for companies, institutions, and organizations to improve workforce productivity and leadership capacity.",
    features: [
      "Corporate training programs",
      "Institutional capacity building",
      "Workforce productivity training",
      "Executive development",
      "Customized curricula",
    ],
    color: "green",
    link: "/b2b-training",
  },
];

const processSteps = [
  {
    icon: MagnifyingGlass,
    title: "Discover",
    description: "We begin by understanding community needs through research, consultations, and participatory assessments.",
  },
  {
    icon: Chalkboard,
    title: "Design",
    description: "We collaborate with communities to design interventions that address identified needs and build on local strengths.",
  },
  {
    icon: Rocket,
    title: "Deliver",
    description: "We implement programs with a focus on quality, participation, and community ownership.",
  },
  {
    icon: Gear,
    title: "Develop",
    description: "We provide ongoing support to ensure skills are applied and enterprises grow sustainably.",
  },
  {
    icon: UsersThree,
    title: "Devolve",
    description: "We gradually transfer ownership and management to communities for long-term sustainability.",
  },
  {
    icon: Medal,
    title: "Document",
    description: "We capture lessons learned and best practices to inform future programs and share knowledge.",
  },
];

export default function ServicesPage() {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-green-950" data-testid="services-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 pattern-dots" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400 mb-4 block">
              Our Services
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-6">
              Comprehensive Interventions for Lasting Impact
            </h1>
            <p className="text-lg text-green-100/80 leading-relaxed">
              Our work combines empirical research, targeted training, leadership development, innovation support, and enterprise strengthening to help communities create sustainable change.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28" data-testid="services-grid-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                    service.color === "green" ? "bg-green-100" : "bg-amber-100"
                  }`}>
                    <service.icon 
                      className={`h-8 w-8 ${service.color === "green" ? "text-green-700" : "text-amber-600"}`} 
                      weight="duotone" 
                    />
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-3">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-3">
                        <CheckCircle 
                          className={`h-5 w-5 shrink-0 ${
                            service.color === "green" ? "text-green-700" : "text-amber-600"
                          }`} 
                          weight="fill" 
                        />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className={`relative ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className={`aspect-[4/3] rounded-2xl overflow-hidden ${
                    service.color === "green" ? "bg-green-50" : "bg-amber-50"
                  }`}>
                    <img 
                      src={
                        index === 0 ? "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/afobkmcc_SCHOOL.png" :
                        index === 1 ? "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0tfpxi02_PP.png" :
                        index === 2 ? "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/0s4xigoz_ICT.png" :
                        index === 3 ? "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/ymbseg05_AGRIC.png" :
                        index === 4 ? "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/fgzorv0v_HP.png" :
                        "https://customer-assets.emergentagent.com/job_peda-enterprises/artifacts/lxiebqhu_55d8f7e2-761d-4c63-9839-a0652352eb5a.jpeg"
                      }
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className={`absolute -bottom-4 -right-4 w-24 h-24 rounded-2xl -z-10 ${
                    service.color === "green" ? "bg-green-200" : "bg-amber-200"
                  }`} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mb-4">
              How We Work
            </h2>
            <p className="text-lg text-slate-600">
              Our systematic approach ensures quality delivery and sustainable outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <step.icon className="h-6 w-6 text-green-700" weight="duotone" />
                  </div>
                  <span className="text-4xl font-bold text-slate-200">{index + 1}</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-green-700 to-green-800 rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Community?
            </h2>
            <p className="text-lg text-green-100/90 mb-8 max-w-2xl mx-auto">
              Contact us to learn how PEDA can support your community development goals through our comprehensive range of services.
            </p>
            <Button 
              asChild 
              size="lg" 
              className="bg-amber-500 hover:bg-amber-600 text-green-950 font-semibold rounded-xl px-8"
              data-testid="services-contact-button"
            >
              <Link to="/contact">
                Get In Touch <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
